<script setup lang="ts">
import { useAppStore } from "@/store/app";
import type {
  PictureChoiceElement,
  PictureChoiceSelectionLimit,
} from "@/types/pictureChoice";
import {
  isPictureChoiceSelectionTypeLocked,
  normalizePictureChoiceElement,
} from "@/util/pictureChoice";
import { InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIInputNumber,
  UISelect,
  UISwitch,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { computed, type PropType } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  element: {
    type: Object as PropType<PictureChoiceElement>,
    required: true,
  },
});

const emit = defineEmits<{
  (event: "update:element", element: PictureChoiceElement): void;
}>();

const store = useAppStore();
const { t } = useI18n();

const isSelectionTypeLocked = computed(() =>
  isPictureChoiceSelectionTypeLocked(props.element)
);
const isMultipleSelection = computed(
  () => props.element.dataType === "CHECKBOX"
);
const selectionLimitOptions = computed(() => [
  {
    label: t("fields.choiceElements.pictureChoice.unlimited"),
    value: "unlimited",
  },
  {
    label: t("fields.choiceElements.pictureChoice.exact"),
    value: "exact",
  },
  {
    label: t("fields.choiceElements.pictureChoice.range"),
    value: "range",
  },
]);

const updateElement = (mutate: (element: PictureChoiceElement) => void) => {
  const nextElement = cloneDeep(props.element);
  mutate(nextElement);
  emit(
    "update:element",
    normalizePictureChoiceElement(nextElement) as PictureChoiceElement
  );
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const updateMultipleSelection = (isMultiple: boolean) => {
  if (isSelectionTypeLocked.value) return;

  updateElement((element) => {
    element.dataType = isMultiple ? "CHECKBOX" : "RADIO";
    element.type = isMultiple ? "checkbox" : "radio";
    if (isMultiple) {
      element.allowCustomOption = false;
      delete element.pictureChoiceSettings.otherOption;
    } else {
      element.pictureChoiceSettings.selectionLimit = {
        mode: "unlimited",
      };
    }
  });
};

const updateSelectionLimitMode = (
  mode: PictureChoiceSelectionLimit["mode"]
) => {
  updateElement((element) => {
    element.pictureChoiceSettings.selectionLimit =
      mode === "exact"
        ? { mode, exact: 1 }
        : mode === "range"
        ? { mode, min: 1, max: 1 }
        : { mode: "unlimited" };
  });
};

const updateSelectionLimitNumber = (
  property: "exact" | "min" | "max",
  value: number | null
) => {
  updateElement((element) => {
    const selectionLimit = element.pictureChoiceSettings
      .selectionLimit as Record<string, unknown>;
    selectionLimit[property] =
      typeof value === "number" && Number.isFinite(value) ? value : null;
  });
};
</script>

<template>
  <div
    v-if="!isSelectionTypeLocked || isMultipleSelection"
    data-testid="picture-choice-multiple-selection-setting"
    class="mb-2 mt-4 space-y-4"
  >
    <div
      v-if="!isSelectionTypeLocked"
      data-testid="picture-choice-multiple-selection-toggle"
      class="flex items-center justify-between gap-4"
    >
      <div class="flex items-center">
        <span class="switch-handle block text-sm font-medium text-gray-700">
          {{ t("fields.choiceElements.pictureChoice.multipleSelection") }}
        </span>
        <UITooltip trigger="hover" placement="top">
          <template #trigger>
            <InfoCircleIcon class="ml-1 h-4 w-4 text-gray-400" />
          </template>
          {{ t("fields.choiceElements.pictureChoice.selectionTypeLocked") }}
        </UITooltip>
      </div>
      <UISwitch
        size="small"
        :value="isMultipleSelection"
        @update:value="updateMultipleSelection"
      />
    </div>

    <template v-if="isMultipleSelection">
      <div class="space-y-1.5">
        <label
          class="block text-sm font-medium text-gray-700"
          for="picture-choice-selection-limit"
        >
          {{ t("fields.choiceElements.pictureChoice.selectionLimit") }}
        </label>
        <UISelect
          id="picture-choice-selection-limit"
          :value="element.pictureChoiceSettings.selectionLimit.mode"
          :options="selectionLimitOptions"
          @update:value="updateSelectionLimitMode"
        />
      </div>

      <div
        v-if="element.pictureChoiceSettings.selectionLimit.mode === 'exact'"
        class="space-y-1.5"
      >
        <label
          class="block text-sm font-medium text-gray-700"
          for="picture-choice-exact-selections"
        >
          {{ t("fields.choiceElements.pictureChoice.exactSelections") }}
        </label>
        <UIInputNumber
          id="picture-choice-exact-selections"
          :model-value="element.pictureChoiceSettings.selectionLimit.exact"
          :min="1"
          :show-button="false"
          size="large"
          @update:model-value="updateSelectionLimitNumber('exact', $event)"
        />
      </div>

      <div
        v-if="element.pictureChoiceSettings.selectionLimit.mode === 'range'"
        class="grid grid-cols-2 gap-3"
      >
        <div class="space-y-1.5">
          <label
            class="block text-sm font-medium text-gray-700"
            for="picture-choice-minimum"
          >
            {{ t("fields.choiceElements.pictureChoice.minimum") }}
          </label>
          <UIInputNumber
            id="picture-choice-minimum"
            :model-value="element.pictureChoiceSettings.selectionLimit.min"
            :min="1"
            :show-button="false"
            size="large"
            @update:model-value="updateSelectionLimitNumber('min', $event)"
          />
        </div>
        <div class="space-y-1.5">
          <label
            class="block text-sm font-medium text-gray-700"
            for="picture-choice-maximum"
          >
            {{ t("fields.choiceElements.pictureChoice.maximum") }}
          </label>
          <UIInputNumber
            id="picture-choice-maximum"
            :model-value="element.pictureChoiceSettings.selectionLimit.max"
            :min="1"
            :show-button="false"
            size="large"
            @update:model-value="updateSelectionLimitNumber('max', $event)"
          />
        </div>
      </div>
    </template>
  </div>
</template>
